import { useState } from "react";
import { Link } from "react-router-dom";
import PageShell from "@/components/feature/PageShell";
import Seo from "@/components/base/Seo";

const contactInfo = [
  { icon: "ri-mail-line", label: "反馈邮箱", value: "hi@guqu.example.com" },
  { icon: "ri-24-hours-line", label: "服务时间", value: "全天候在线" },
  { icon: "ri-message-2-line", label: "反馈类型", value: "投稿 / 建议 / 其他" },
];

const topics = ["内容投稿", "功能建议", "内容反馈", "合作洽谈", "其他"];

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    // 蜜罐字段校验：被机器人填充则不提交，但展示与正常提交一致的反馈
    const honeypot = String(data.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    setStatus("submitting");
    setFormError("");

    const payload = new URLSearchParams();
    data.forEach((value, key) => {
      if (key === "website_alt") return;
      payload.append(key, String(value));
    });

    try {
      const res = await fetch("https://readdy.ai/api/form/da28ublv7gurs51ef990", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || responseText;
      if (res.ok && parsed?.code === "OK") {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后再试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请检查网络后重试。");
    }
  };

  return (
    <PageShell>
      <Seo
        title="联系我们 - 投稿与意见反馈 | 瓜趣视频"
        description="有任何内容投稿、功能建议或合作想法？通过瓜趣视频联系页面即可快速反馈，我们全天候在线，会认真倾听每一位用户的声音。"
        keywords="联系我们,意见反馈,内容投稿,合作洽谈,瓜趣视频"
        canonical="https://gagplay6.github.io/contact"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "ContactPage",
            name: "联系瓜趣视频",
            url: "https://gagplay6.github.io/contact",
            description: "瓜趣视频投稿与意见反馈",
            inLanguage: "zh-CN",
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://gagplay6.github.io/" },
              { "@type": "ListItem", position: 2, name: "联系我们", item: "https://gagplay6.github.io/contact" },
            ],
          },
        ]}
      />

      <div className="container-x py-8">
        <nav className="flex items-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑导航">
          <Link to="/" className="hover:text-primary-600">
            首页
          </Link>
          <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
          <span className="text-foreground-700">联系我们</span>
        </nav>

        <section className="mt-8 grid gap-8 lg:grid-cols-5" aria-labelledby="contact-title">
          <div className="lg:col-span-2">
            <span className="chip bg-primary-100 text-primary-700">联系我们</span>
            <h1 id="contact-title" className="font-heading mt-4 text-2xl text-foreground-950 md:text-3xl">
              告诉我们你的想法
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-foreground-700">
              无论是想投稿有趣的内容、反馈观看体验，还是洽谈合作，都可以通过右侧表单快速联系我们。我们会在收到后尽快回复。
            </p>

            <div className="mt-6 space-y-4">
              {contactInfo.map((item) => (
                <div key={item.label} className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                    <i className={`${item.icon} text-lg`} aria-hidden="true"></i>
                  </span>
                  <div>
                    <div className="text-xs text-foreground-500">{item.label}</div>
                    <div className="text-sm font-semibold text-foreground-950">{item.value}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="card-surface mt-6 p-5">
              <h2 className="text-sm font-bold text-foreground-950">温馨提示</h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                感谢你对瓜趣视频的支持与关注。我们会认真对待每一份反馈，让平台越做越好，让好内容更容易被看到。
              </p>
            </div>
          </div>

          <div className="card-surface p-6 lg:col-span-3">
            {status === "success" ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-accent-100 text-accent-600">
                  <i className="ri-check-line text-3xl" aria-hidden="true"></i>
                </span>
                <h2 className="mt-4 text-lg font-bold text-foreground-950">提交成功</h2>
                <p className="mt-2 text-sm text-foreground-600">感谢你的反馈，我们会尽快处理并回复。</p>
                <button
                  type="button"
                  onClick={() => setStatus("idle")}
                  className="btn-secondary mt-6"
                >
                  再写一条
                </button>
              </div>
            ) : (
              <form
                id="contact-form"
                data-readdy-form="瓜趣视频意见反馈表单"
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-foreground-700">
                      您的称呼
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      placeholder="怎么称呼您"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm outline-none transition-colors focus:border-primary-400"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground-700">
                      邮箱地址
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      placeholder="用于接收回复"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm outline-none transition-colors focus:border-primary-400"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="topic" className="mb-1.5 block text-sm font-medium text-foreground-700">
                    反馈类型
                  </label>
                  <select
                    id="topic"
                    name="topic"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm outline-none transition-colors focus:border-primary-400"
                  >
                    {topics.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="mb-1.5 block text-sm font-medium text-foreground-700">
                    留言内容
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="写下你的建议、投稿或想法..."
                    className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm outline-none transition-colors focus:border-primary-400"
                  ></textarea>
                  <p className="mt-1 text-right text-xs text-foreground-400">最多 500 字</p>
                </div>

                {/* 反垃圾蜜罐字段 */}
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                {status === "error" && formError && (
                  <p className="rounded-md border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-800" role="alert">
                    {formError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="btn-primary w-full disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {status === "submitting" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" aria-hidden="true"></i>
                      提交中...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-fill" aria-hidden="true"></i>
                      提交反馈
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </section>
      </div>
    </PageShell>
  );
}